import { useCallback, useEffect, useRef } from 'react'

export interface AbortResetClient {
  resetController: () => {
    controller: AbortController
    requestId: number
  }
}

export const useAbortController = () => {
  const requestIdRef = useRef(0)
  const abortControllerRef = useRef<AbortController | null>(null)

  const resetController = useCallback(() => {
    // Cancel current request
    if (abortControllerRef.current) {
      abortControllerRef.current.abort()
    }

    // Create new controller and update requestId
    const controller = new AbortController()
    abortControllerRef.current = controller
    requestIdRef.current += 1
    return { controller, requestId: requestIdRef.current }
  }, [])

  const isCurrentRequest = (id: number) => id === requestIdRef.current

  // Abort on component unmount
  useEffect(() => {
    return () => {
      if (abortControllerRef.current) {
        abortControllerRef.current.abort()
      }
    }
  }, [])

  return {
    abort: () => abortControllerRef.current?.abort(),
    isCurrentRequest,
    get requestId() {
      return requestIdRef.current
    },
    resetController,
    get signal() {
      return abortControllerRef.current?.signal
    },
  }
}
